import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Re-themed 2026-05-27: NAVY/ORANGE constants kept for minimal diff, but now point to CAAM v2 palette.
// NAVY -> primary gold (var(--caam-accent), defined :root in App.css). ORANGE -> earthtone terracotta (same as Gmail/Drive error color).
const NAVY = 'var(--caam-accent)';
const ORANGE = '#9B3A1F';
const API = process.env.REACT_APP_API_URL;
const tk = () => localStorage.getItem('token');
const hdrs = () => ({ Authorization: 'Bearer ' + tk() });

function formatDate(d) {
  const date = new Date(d), now = new Date(), diff = now - date;
  const m = Math.floor(diff/60000), h = Math.floor(diff/3600000), days = Math.floor(diff/86400000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

const AVATAR_COLORS = ['#1A3A5C', '#EA6F2E', '#0F766E', '#7C3AED', '#B45309', '#1D4ED8'];
const avatarColor = (id) => AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];

// Resolve the best avatar source for a staff member:
//   1. google_photo_url (CDN absolute URL from Google Workspace, captured at OAuth login)
//   2. photo_url (manual upload via StaffDirectory — server-relative, needs API prefix)
//   3. null (caller renders the colored-initials fallback)
function getAvatarSrc(staff) {
  if (!staff) return null;
  if (staff.google_photo_url) return staff.google_photo_url;
  if (staff.photo_url) return `${API}${staff.photo_url}`;
  return null;
}

// Compact reusable avatar: shows the Workspace headshot if available, else a colored circle
// with the person's initials. Pass staffList so we can look up the photo by id.
function StaffAvatar({ staffId, staffList = [], firstName, lastName, size = 32, fontSize }) {
  const staff = staffList.find(s => s.id === staffId);
  const src = getAvatarSrc(staff);
  const initials = ((staff?.first_name || firstName || '')[0] || '') +
                   ((staff?.last_name  || lastName  || '')[0] || '');
  const base = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    flexShrink: 0,
    overflow: 'hidden',
  };
  if (src) {
    return (
      <div style={base}>
        <img
          src={src}
          alt={initials}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }
  return (
    <div style={{
      ...base,
      background: avatarColor(staffId),
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: fontSize || Math.max(9, Math.round(size * 0.36)) + 'px',
      fontWeight: 700,
      textTransform: 'uppercase',
    }}>
      {initials || '?'}
    </div>
  );
}

const typeColors = {
  onboarding:     { bg: '#EFF6FF', color: '#1E40AF' },
  welcome_letter: { bg: '#F0FDF4', color: '#065F46' },
  system:         { bg: '#FFF7ED', color: '#92400E' },
  message:        { bg: '#F5F3FF', color: '#5B21B6' },
};

function TypeIcon({ type, size = 14 }) {
  if (type === 'onboarding') return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>;
  if (type === 'welcome_letter') return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>;
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
}

function NotificationsTab({ navigateTo, onCountChange }) {
  const [notifications, setNotifications] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchNotifs(); }, [filter]);

  const fetchNotifs = async () => {
    try {
      const params = filter === 'unread' ? { unread_only: 'true' } : {};
      const res = await axios.get(API + '/notifications', { headers: hdrs(), params });
      setNotifications(res.data);
      onCountChange(res.data.filter(n => !n.is_read).length);
      setLoading(false);
    } catch (e) { setLoading(false); }
  };

  const open = async (n) => {
    setSelected(n);
    try {
      await axios.post(API + '/notifications/' + n.id + '/read', {}, { headers: hdrs() });
      setNotifications(prev => prev.filter(x => x.id !== n.id));
      onCountChange(notifications.filter(x => !x.is_read && x.id !== n.id).length);
    } catch (e) {}
  };

  const clearAll = async () => {
    try {
      await axios.delete(API + '/notifications/clear-all', { headers: hdrs() });
      setNotifications([]);
      onCountChange(0);
    } catch (e) {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(API + '/notifications/read-all', {}, { headers: hdrs() });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      onCountChange(0);
    } catch (e) {}
  };

  const unread = notifications.filter(n => !n.is_read).length;

  if (selected) return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      <div style={{ padding:'12px 14px', borderBottom:'1px solid #E5E7EB' }}>
        <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#6B7280', display:'flex', alignItems:'center', gap:'4px', fontSize:'12px', padding:0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back
        </button>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'16px 14px' }}>
        {(() => { const tc = typeColors[selected.type] || typeColors.message; return (
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
            <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:tc.bg, color:tc.color, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <TypeIcon type={selected.type} size={13}/>
            </div>
            <span style={{ fontSize:'10px', fontWeight:'600', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.05em' }}>{selected.type?.replace('_',' ')}</span>
          </div>
        ); })()}
        <h3 style={{ fontSize:'14px', fontWeight:'700', color:'#111827', margin:'0 0 6px 0' }}>{selected.subject}</h3>
        <div style={{ fontSize:'11px', color:'#9CA3AF', marginBottom:'14px', paddingBottom:'14px', borderBottom:'1px solid #F3F4F6' }}>
          {selected.sender_first_name && <span>{selected.sender_first_name} {selected.sender_last_name} · </span>}
          {new Date(selected.created_at).toLocaleString()}
        </div>
        <div style={{ fontSize:'13px', color:'#374151', lineHeight:'1.7', whiteSpace:'pre-wrap' }}>{selected.message}</div>
        {selected.type === 'onboarding' && navigateTo && selected.metadata && (selected.metadata.case_id || selected.metadata?.case_id) && (
          <button onClick={() => navigateTo('onboarding', selected.metadata?.case_id || null)}
            style={{ marginTop:'16px', display:'inline-flex', alignItems:'center', gap:'6px', padding:'8px 14px', background:NAVY, color:'#fff', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/></svg>
            View Onboarding Tasks
          </button>
        )}
        {selected.type === 'time_off' && navigateTo && (
          <button onClick={() => navigateTo('time-off')}
            style={{ marginTop:'16px', display:'inline-flex', alignItems:'center', gap:'6px', padding:'8px 14px', background:NAVY, color:'#fff', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Review Request
          </button>
        )}
        {selected.type === 'ticket' && navigateTo && (
          <button onClick={() => navigateTo(selected.metadata?.agent ? 'helpdesk' : 'get-help')}
            style={{ marginTop:'16px', display:'inline-flex', alignItems:'center', gap:'6px', padding:'8px 14px', background:NAVY, color:'#fff', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Open Helpdesk
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      <div style={{ padding:'10px 14px', borderBottom:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', gap:'3px', background:'#F3F4F6', borderRadius:'6px', padding:'2px' }}>
          {['all','unread'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding:'4px 10px', fontSize:'11px', fontWeight:filter===f?'600':'400', color:filter===f?'#fff':'#6B7280', background:filter===f?NAVY:'transparent', border:'none', borderRadius:'4px', cursor:'pointer' }}>
              {f === 'all' ? 'All' : `Unread${unread > 0 ? ' ('+unread+')' : ''}`}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:'6px' }}>
          {unread > 0 && <button onClick={markAllRead} style={{ fontSize:'11px', color:'#6B7280', background:'none', border:'1px solid #E5E7EB', borderRadius:'4px', padding:'3px 8px', cursor:'pointer' }}>Mark all read</button>}
          {notifications.length > 0 && <button onClick={clearAll} style={{ fontSize:'11px', color:'#EF4444', background:'none', border:'1px solid #FECACA', borderRadius:'4px', padding:'3px 8px', cursor:'pointer' }}>Clear all</button>}
        </div>
      </div>
      <div style={{ flex:1, overflowY:'auto' }}>
        {loading ? <div style={{ padding:'40px', textAlign:'center', fontSize:'12px', color:'#9CA3AF' }}>Loading...</div>
        : notifications.length === 0 ? (
          <div style={{ padding:'40px 20px', textAlign:'center' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ marginBottom:'8px' }}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
            <p style={{ fontSize:'12px', color:'#9CA3AF' }}>No notifications</p>
          </div>
        ) : notifications.map(n => {
          const tc = typeColors[n.type] || typeColors.message;
          return (
            <div key={n.id} onClick={() => open(n)} style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6', cursor:'pointer', background:n.is_read?'#fff':'#FAFBFF', borderLeft:n.is_read?'2px solid transparent':`2px solid ${ORANGE}` }}
              onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background=n.is_read?'#fff':'#FAFBFF'}>
              <div style={{ display:'flex', gap:'8px', alignItems:'flex-start' }}>
                <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:tc.bg, color:tc.color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <TypeIcon type={n.type} size={13}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'2px' }}>
                    <span style={{ fontSize:'12px', fontWeight:n.is_read?'500':'700', color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'140px' }}>{n.subject}</span>
                    <span style={{ fontSize:'10px', color:'#9CA3AF', flexShrink:0, marginLeft:'4px' }}>{formatDate(n.created_at)}</span>
                  </div>
                  <p style={{ fontSize:'11px', color:'#6B7280', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{n.message}</p>
                </div>
                {!n.is_read && <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:ORANGE, flexShrink:0, marginTop:'5px' }}/>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Small clickable chip standing in for an email attached to / referenced by a chat message.
// Clicking it opens the email in the 3rd (reading) pane; the optional ✕ detaches a pending one.
// `own` styles the chip for the sender's gold bubble (light-on-dark) vs a received grey bubble.
function EmailChip({ email, own, onClick, onRemove }) {
  if (!email) return null;
  return (
    <div onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:'7px', marginTop:'5px', padding:'6px 9px',
        background: own ? 'rgba(255,255,255,0.18)' : '#fff',
        border:'1px solid ' + (own ? 'rgba(255,255,255,0.4)' : '#E5E7EB'),
        borderRadius:'8px', cursor:'pointer', maxWidth:'100%' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={own ? '#fff' : NAVY} strokeWidth="1.8" style={{ flexShrink:0 }}>
        <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,6 12,13 2,6"/>
      </svg>
      <span style={{ fontSize:'11px', fontWeight:'600', color: own ? '#fff' : '#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {email.subject || '(no subject)'}
      </span>
      {onRemove && (
        <span onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{ marginLeft:'auto', color:'#9CA3AF', fontSize:'13px', lineHeight:1, flexShrink:0, paddingLeft:'4px' }}>✕</span>
      )}
    </div>
  );
}

export function ChatTab({ currentUser, staffList, initialMention, initialMessage, attachEmail, onRead }) {
  const [conversations, setConversations] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [composing, setComposing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingEmail, setPendingEmail] = useState(null);  // email to attach to the next message
  const [viewingEmail, setViewingEmail] = useState(null);  // email shown in the 3rd (reading) pane
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    const poll = setInterval(fetchConversations, 5000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (initialMention && staffList.length > 0) {
      const match = staffList.find(s => (s.first_name + ' ' + s.last_name) === initialMention);
      if (match) { openConversation(match); if (initialMessage) setInput(initialMessage); }
    }
  }, [initialMention, initialMessage, staffList]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (selectedStaff) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => fetchMessages(selectedStaff.id, false), 3000);
      return () => clearInterval(pollRef.current);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [selectedStaff]);

  useEffect(() => {
    if (composing && searchRef.current) searchRef.current.focus();
  }, [composing]);

  // When the user clicks "Discuss" on an email (App passes the snapshot via attachEmail),
  // stage it as the attachment for the next message and open it in the 3rd reading pane so
  // the sender sees exactly what they're about to share. Cleared once the message is sent.
  useEffect(() => {
    if (attachEmail) {
      setPendingEmail(attachEmail);
      setViewingEmail(attachEmail);
    }
  }, [attachEmail]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(API + '/chat/conversations', { headers: hdrs() });
      setConversations(res.data);
    } catch (e) {}
  };

  const fetchMessages = async (otherId, scroll = true) => {
    try {
      const res = await axios.get(API + '/chat/messages/' + otherId, { headers: hdrs() });
      setMessages(res.data);
      // This GET marks the conversation read server-side; refresh the sidebar unread badge.
      if (onRead) onRead();
    } catch (e) {}
  };

  const openConversation = (staff) => {
    setSelectedStaff(staff);
    setComposing(false);
    setSearchQuery('');
    setInput('');
    fetchMessages(staff.id);
  };

  // Delete (clear) a conversation from MY view only. Server keeps the messages (admin can
  // still pull a transcript); the thread reappears for me if the other person messages again.
  const deleteConversation = async (otherId, e) => {
    if (e) e.stopPropagation();  // don't also open the conversation
    if (!window.confirm('Delete this conversation from your view? The other person keeps their copy, and it will reappear if they message you again.')) return;
    try {
      await axios.delete(API + '/chat/conversations/' + otherId, { headers: hdrs() });
      setConversations(prev => prev.filter(c => c.other_id !== otherId));
      if (selectedStaff && selectedStaff.id === otherId) { setSelectedStaff(null); setMessages([]); }
    } catch (err) { /* ignore — next poll will reconcile */ }
  };

  const sendMessage = async () => {
    if (!input.trim() || !selectedStaff || sending) return;
    setSending(true);
    try {
      const res = await axios.post(API + '/chat/messages',
        { recipient_id: selectedStaff.id, message: input.trim(), email_ref: pendingEmail || undefined },
        { headers: hdrs() }
      );
      setMessages(m => [...m, {
        ...res.data,
        sender_first: currentUser?.first_name,
        sender_last: currentUser?.last_name
      }]);
      setInput('');
      setPendingEmail(null);  // attachment consumed; don't ride along on the next message
      fetchConversations();
    } catch (e) {}
    finally { setSending(false); }
  };

  const filteredStaff = (staffList || [])
    .filter(s => s.id !== currentUser?.id)
    .filter(s => (s.first_name + ' ' + s.last_name).toLowerCase().includes(searchQuery.toLowerCase()));

  // Default the right pane to the most recent conversation on load.
  useEffect(() => {
    if (!selectedStaff && !initialMention && conversations.length > 0) {
      const c = conversations[0];
      setSelectedStaff({ id: c.other_id, first_name: c.first_name, last_name: c.last_name });
      fetchMessages(c.other_id);
    }
  }, [conversations]);

  const peer = selectedStaff;

  // Two-pane layout: left 1/3 = people/conversations, right 2/3 = active thread.
  return (
    <div style={{ display:'flex', height:'100%', background:'#fff' }}>
      {/* LEFT 1/3 — conversations + people search */}
      <div style={{ width:'33%', minWidth:'260px', maxWidth:'380px', borderRight:'1px solid #E5E7EB', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #E5E7EB' }}>
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search people to start a chat…"
            style={{ width:'100%', padding:'8px 11px', border:'1px solid #E5E7EB', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box' }} />
        </div>
        <div style={{ flex:1, overflowY:'auto' }}>
          {searchQuery ? (
            filteredStaff.length === 0 ? (
              <div style={{ padding:'20px', fontSize:'12px', color:'#9CA3AF', textAlign:'center' }}>No matches</div>
            ) : filteredStaff.map(s => (
              <div key={s.id} onClick={() => openConversation(s)}
                style={{ padding:'10px 14px', borderBottom:'1px solid #F3F4F6', cursor:'pointer', display:'flex', alignItems:'center', gap:'10px' }}
                onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <StaffAvatar staffId={s.id} staffList={staffList} firstName={s.first_name} lastName={s.last_name} size={36} fontSize="12px" />
                <span style={{ fontSize:'13px', fontWeight:'500', color:'#111827' }}>{s.first_name} {s.last_name}</span>
              </div>
            ))
          ) : conversations.length === 0 ? (
            <div style={{ padding:'40px 20px', textAlign:'center' }}>
              <p style={{ fontSize:'13px', fontWeight:'600', color:'#6B7280', margin:'0 0 6px' }}>No conversations yet</p>
              <p style={{ fontSize:'12px', color:'#9CA3AF', margin:0 }}>Search above to message a colleague.</p>
            </div>
          ) : (
            conversations.map(c => {
              const unread = parseInt(c.unread_count) || 0;
              const active = selectedStaff && selectedStaff.id === c.other_id;
              return (
                <div key={c.other_id} onClick={() => openConversation({ id: c.other_id, first_name: c.first_name, last_name: c.last_name })}
                  style={{ padding:'12px 14px', borderBottom:'1px solid #F3F4F6', cursor:'pointer', background: active ? '#FAEEDA' : (unread > 0 ? '#FAFBFF' : '#fff'), borderLeft: active ? '3px solid '+ORANGE : (unread > 0 ? '3px solid '+ORANGE : '3px solid transparent') }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background='#F9FAFB'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background= unread > 0 ? '#FAFBFF' : '#fff'; }}>
                  <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                    <div style={{ position:'relative', flexShrink:0 }}>
                      <StaffAvatar staffId={c.other_id} staffList={staffList} firstName={c.first_name} lastName={c.last_name} size={40} fontSize="13px" />
                      {unread > 0 && <span style={{ position:'absolute', top:'-2px', right:'-2px', minWidth:'15px', height:'15px', padding:'0 3px', borderRadius:'8px', background:ORANGE, color:'#fff', fontSize:'9px', fontWeight:'700', display:'flex', alignItems:'center', justifyContent:'center', boxSizing:'border-box' }}>{unread}</span>}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'2px' }}>
                        <span style={{ fontSize:'13px', fontWeight: unread > 0 ? '700' : '600', color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.first_name} {c.last_name}</span>
                        <span style={{ fontSize:'10px', color:'#9CA3AF', flexShrink:0, marginLeft:'6px' }}>{c.last_message_at ? new Date(c.last_message_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : ''}</span>
                      </div>
                      <div style={{ fontSize:'12px', color: unread > 0 ? '#374151' : '#9CA3AF', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight: unread > 0 ? '600' : '400' }}>
                        {c.last_sender_id === currentUser?.id ? 'You: ' : ''}{c.last_message}
                      </div>
                    </div>
                    <button onClick={(e) => deleteConversation(c.other_id, e)} title="Delete this conversation (your view only)"
                      style={{ flexShrink:0, background:'none', border:'none', cursor:'pointer', color:'#C7CBD1', padding:'4px', borderRadius:'6px', display:'flex', alignItems:'center' }}
                      onMouseEnter={e => { e.currentTarget.style.color = ORANGE; e.currentTarget.style.background = '#FDECEC'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#C7CBD1'; e.currentTarget.style.background = 'none'; }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT 2/3 — active conversation (defaults to most recent) */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
        {peer ? (
          <>
            <div style={{ padding:'12px 18px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', gap:'10px' }}>
              <StaffAvatar staffId={peer.id} staffList={staffList} firstName={peer.first_name} lastName={peer.last_name} size={34} fontSize="12px" />
              <div style={{ fontSize:'15px', fontWeight:'600', color:'#111827' }}>{peer.first_name} {peer.last_name}</div>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'16px 18px', display:'flex', flexDirection:'column', gap:'10px' }}>
              {messages.length === 0 && (
                <div style={{ textAlign:'center', marginTop:'40px', color:'#9CA3AF', fontSize:'12px' }}>No messages yet — say hello to {peer.first_name}!</div>
              )}
              {messages.map(msg => {
                const own = msg.sender_id === currentUser?.id;
                const timeStr = new Date(msg.created_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
                return (
                  <div key={msg.id} style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexDirection: own ? 'row-reverse' : 'row' }}>
                    <StaffAvatar staffId={own ? currentUser?.id : peer.id} staffList={staffList} firstName={msg.sender_first} lastName={msg.sender_last} size={28} fontSize="10px" />
                    <div style={{ maxWidth:'70%' }}>
                      <div style={{ fontSize:'10px', color:'#9CA3AF', marginBottom:'2px', textAlign: own ? 'right' : 'left' }}>{timeStr}</div>
                      <div style={{ padding:'9px 13px', borderRadius: own ? '12px 12px 2px 12px' : '12px 12px 12px 2px', background: own ? NAVY : '#F3F4F6', color: own ? '#fff' : '#111827', fontSize:'13px', lineHeight:'1.5', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{msg.message}</div>
                      {msg.email_ref && <EmailChip email={msg.email_ref} own={own} onClick={() => setViewingEmail(msg.email_ref)} />}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef}/>
            </div>
            <div style={{ padding:'12px 18px', borderTop:'1px solid #E5E7EB' }}>
              {pendingEmail && (
                <div style={{ marginBottom:'8px' }}>
                  <div style={{ fontSize:'10px', color:'#9CA3AF', marginBottom:'3px' }}>Attaching email — {peer.first_name} can open it in a reading pane</div>
                  <EmailChip email={pendingEmail} onClick={() => setViewingEmail(pendingEmail)} onRemove={() => setPendingEmail(null)} />
                </div>
              )}
              <div style={{ display:'flex', gap:'8px', alignItems:'flex-end' }}>
                <textarea value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={'Message ' + peer.first_name + '…'}
                  style={{ flex:1, padding:'10px 12px', border:'1px solid #E5E7EB', borderRadius:'10px', fontSize:'13px', resize:'none', outline:'none', lineHeight:'1.5', boxSizing:'border-box', fontFamily:'inherit', maxHeight:'120px' }}
                  rows={1}
                />
                <button onClick={sendMessage} disabled={sending}
                  style={{ width:'38px', height:'38px', borderRadius:'10px', background:NAVY, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, opacity: sending ? 0.5 : 1 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
              </div>
              <div style={{ marginTop:'4px', fontSize:'10px', color:'#D1D5DB' }}>Enter to send · Shift+Enter for new line</div>
            </div>
          </>
        ) : (
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#9CA3AF' }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ marginBottom:'12px' }}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            <p style={{ fontSize:'13px', margin:0 }}>Select a conversation to start chatting</p>
          </div>
        )}
      </div>

      {/* THIRD PANE — email reading pane. Opens when a chip (pending or on a message) is clicked. */}
      {viewingEmail && (
        <div style={{ width:'40%', minWidth:'320px', maxWidth:'480px', borderLeft:'1px solid #E5E7EB', display:'flex', flexDirection:'column', background:'#FCFCFD' }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px' }}>
            <span style={{ fontSize:'10px', fontWeight:'700', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.05em' }}>Email</span>
            <button onClick={() => setViewingEmail(null)} title="Close" style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:'16px', lineHeight:1, padding:'2px' }}>✕</button>
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:'16px' }}>
            <h3 style={{ fontSize:'15px', fontWeight:'700', color:'#111827', margin:'0 0 10px' }}>{viewingEmail.subject || '(no subject)'}</h3>
            <div style={{ fontSize:'11px', color:'#6B7280', marginBottom:'4px' }}><strong style={{ color:'#374151' }}>From:</strong> {viewingEmail.from || '—'}</div>
            {viewingEmail.date && <div style={{ fontSize:'11px', color:'#6B7280', marginBottom:'14px' }}><strong style={{ color:'#374151' }}>Date:</strong> {viewingEmail.date}</div>}
            {/* Email bodies are HTML (same source Gmail renders) — sandbox them in an iframe.
                Rendering as plain text would dump raw markup ("ASCII") on screen. */}
            <div style={{ borderTop:'1px solid #F3F4F6', paddingTop:'14px' }}>
              <iframe
                title="Email body"
                srcDoc={viewingEmail.body || '<p style="color:#9CA3AF">(no content)</p>'}
                sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                onLoad={(e) => { try { const d = e.target.contentDocument; if (d && d.body) e.target.style.height = Math.max(120, d.body.scrollHeight + 24) + 'px'; } catch (err) {} }}
                style={{ width:'100%', border:'none', minHeight:'120px', background:'#fff' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================
// RightPane — toggles between Chat and Notifications
// The header bell switches modes; pane defaults to Chat.
// ============================================
export default function RightPane({ navigateTo, currentUser, staffList, onChatTrigger, setChatTrigger, paneMode, setPaneMode, onUnreadCountChange }) {
  const [initialMention, setInitialMention] = useState(null);
  const [unreadChat, setUnreadChat] = useState(0);

  // Default to chat
  const mode = paneMode || 'chat';

  // When App.js triggers a chat (e.g. from a row's "Message" button) capture it
  useEffect(() => {
    if (onChatTrigger) {
      setInitialMention(onChatTrigger);
      if (setPaneMode) setPaneMode('chat');  // Force chat mode when a chat is triggered
      setChatTrigger && setChatTrigger(null);
    }
  }, [onChatTrigger, setChatTrigger, setPaneMode]);

  return (
    <aside style={styles.pane}>
      {mode === 'chat' ? (
        <ChatTab
          currentUser={currentUser}
          staffList={staffList}
          initialMention={initialMention}
        />
      ) : (
        <NotificationsTab
          navigateTo={navigateTo}
          onCountChange={(c) => { if (onUnreadCountChange) onUnreadCountChange(c); }}
        />
      )}
    </aside>
  );
}

const styles = {
  pane: {
    width: 280,
    background: '#ffffff',
    borderLeft: '0.5px solid var(--caam-border)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    height: '100%',
    overflow: 'hidden'
  }
};
